import React from 'react';
import theme from '../theme.config';

/**
 * HeroVisual — site-specific decorative visual that replaces the legacy mascot.
 * Variant is driven by `theme.style.heroVisual` (string token). Each variant
 * is pure CSS/SVG, no external image assets needed.
 *
 * Tokens supported:
 *   - 'controller-orb'   (default — concentric pulsing rings)
 *   - 'circuit-grid'     (cyberpunk grid + scanline)
 *   - 'pixel-tiles'      (8-bit blocky tiles)
 *   - 'gold-orbs'        (premium glass orbs)
 *   - 'crosshair-radar'  (tactical radar sweep)
 *   - 'deep-abyss'       (deep-sea jellyfish + bioluminescent particles)
 *   - 'terminal-feed'    (matrix code rain on black + terminal log)
 *   - 'dune-caravan'     (desert nomad — dunes + camel silhouette + setting sun)
 *   - 'molecule-orbit'   (clean lab — atom with electron orbitals)
 *   - 'rune-circle'      (viking forge — engraved rune disc)
 *   - 'enso-ink'         (Japanese zen — enso brush circle + falling petal)
 *   - 'sun-horizon'      (synthwave — retro sun grid horizon)
 *   - 'candle-vigil'     (gothic — candle flame + wax drips + cobweb)
 */
const VARIANT = (theme.style && theme.style.heroVisual) || 'controller-orb';

function ControllerOrb() {
  return (
    <div className="relative w-80 h-80">
      <div className="absolute inset-0 rounded-full bg-primary/20 blur-3xl animate-pulse" />
      <div className="absolute inset-6 rounded-full border-2 border-primary/40 animate-spin-slow" />
      <div className="absolute inset-14 rounded-full border-2 border-accent/40" />
      <div className="absolute inset-24 rounded-full bg-gradient-to-br from-primary to-accent shadow-2xl" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-32 h-32 rounded-full border-4 border-white/10 backdrop-blur-sm bg-gradient-to-tr from-primary/30 to-accent/30" />
      </div>
    </div>
  );
}

function CircuitGrid() {
  return (
    <div className="relative w-80 h-80 overflow-hidden rounded-2xl border border-primary/30 bg-black/40">
      <div
        className="absolute inset-0 opacity-60"
        style={{
          backgroundImage:
            'linear-gradient(hsl(var(--primary) / 0.4) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary) / 0.4) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-tr from-secondary/30 via-transparent to-accent/40" />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-full bg-accent/60 blur-2xl animate-pulse" />
      <div className="absolute left-0 right-0 h-1 bg-primary/70 animate-scan" />
      <div className="absolute right-4 bottom-4 font-mono text-xs text-primary/80 tracking-widest">
        // {theme.brand.short.toUpperCase()}_GRID.SYS
      </div>
    </div>
  );
}

function PixelTiles() {
  const tiles = Array.from({ length: 36 });
  const palette = ['bg-primary', 'bg-secondary', 'bg-accent', 'bg-success', 'bg-background'];
  return (
    <div className="relative w-80 h-80 grid grid-cols-6 grid-rows-6 gap-1 p-3 bg-black/40 rounded-xl border-4 border-primary/60 shadow-[8px_8px_0_0_hsl(var(--secondary))]">
      {tiles.map((_, i) => (
        <div
          key={i}
          className={`${palette[(i * 7) % palette.length]} animate-pixel-pulse`}
          style={{ animationDelay: `${(i % 12) * 0.08}s` }}
        />
      ))}
    </div>
  );
}

function GoldOrbs() {
  return (
    <div className="relative w-80 h-80">
      <div className="absolute left-10 top-10 w-40 h-40 rounded-full bg-gradient-to-br from-primary to-accent opacity-80 blur-sm shadow-2xl" />
      <div className="absolute right-6 bottom-12 w-28 h-28 rounded-full bg-gradient-to-br from-accent/70 to-primary/40 backdrop-blur-md border border-primary/30" />
      <div className="absolute right-16 top-6 w-20 h-20 rounded-full border-2 border-primary/60" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="font-serif italic text-7xl text-primary/40 select-none">
          {theme.brand.short[0]}
        </div>
      </div>
    </div>
  );
}

function CrosshairRadar() {
  return (
    <div className="relative w-80 h-80 rounded-full border-2 border-primary/60 bg-radial-radar overflow-hidden">
      {[0.85, 0.65, 0.45, 0.25].map((scale, i) => (
        <div
          key={i}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary/40"
          style={{ width: `${scale * 100}%`, height: `${scale * 100}%` }}
        />
      ))}
      <div className="absolute left-1/2 top-0 bottom-0 w-px bg-primary/40 -translate-x-1/2" />
      <div className="absolute top-1/2 left-0 right-0 h-px bg-primary/40 -translate-y-1/2" />
      <div
        className="absolute left-1/2 top-1/2 w-1/2 h-px origin-left animate-radar-sweep"
        style={{ background: 'linear-gradient(to right, hsl(var(--accent)), transparent)' }}
      />
      <div className="absolute left-[60%] top-[35%] w-2 h-2 rounded-full bg-secondary animate-pulse" />
      <div className="absolute left-[30%] top-[60%] w-2 h-2 rounded-full bg-accent animate-pulse" />
      <div className="absolute right-4 top-4 font-mono text-xs text-primary/80 tracking-widest">
        [ {theme.brand.short.toUpperCase()}-RADAR ]
      </div>
    </div>
  );
}

function DeepAbyss() {
  const particles = Array.from({ length: 18 });
  const tentacles = [30, 50, 70, 90, 110];
  const smallTentacles = [14, 26, 38, 50];
  return (
    <div className="relative w-80 h-80 rounded-3xl overflow-hidden bg-gradient-to-b from-[#001018] via-[#000812] to-black border border-primary/30">
      <div className="absolute inset-0 bg-gradient-to-b from-primary/10 to-transparent" />
      <div className="absolute -top-10 left-10 w-2 h-72 bg-gradient-to-b from-primary/40 to-transparent rotate-6 blur-sm" />
      <div className="absolute -top-10 right-20 w-1 h-64 bg-gradient-to-b from-primary/30 to-transparent -rotate-3 blur-sm" />
      <div className="absolute -top-10 left-40 w-1.5 h-60 bg-gradient-to-b from-secondary/30 to-transparent rotate-12 blur" />
      {particles.map((_, i) => {
        const left = (i * 53) % 100;
        const top = (i * 37 + 7) % 100;
        const size = 2 + (i % 3);
        return (
          <span
            key={i}
            className="absolute rounded-full bg-primary animate-pulse"
            style={{
              left: `${left}%`,
              top: `${top}%`,
              width: `${size}px`,
              height: `${size}px`,
              boxShadow: '0 0 8px currentColor, 0 0 14px currentColor',
              animationDelay: `${(i * 0.4) % 5}s`,
              animationDuration: `${2 + (i % 3)}s`,
            }}
          />
        );
      })}
      <div className="absolute left-1/2 top-[28%] -translate-x-1/2 animate-float-slow">
        <svg width="140" height="160" viewBox="0 0 140 160">
          <defs>
            <radialGradient id="jelly1" cx="50%" cy="35%" r="60%">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.9" />
              <stop offset="60%" stopColor="hsl(var(--primary))" stopOpacity="0.35" />
              <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity="0" />
            </radialGradient>
          </defs>
          <path d="M20 60 Q70 -10 120 60 L120 70 Q70 60 20 70 Z" fill="url(#jelly1)" />
          <path d="M30 50 Q70 0 110 50" fill="none" stroke="white" strokeOpacity="0.35" strokeWidth="1" />
          <path d="M40 40 Q70 8 100 40" fill="none" stroke="white" strokeOpacity="0.2" strokeWidth="1" />
          {tentacles.map((x, i) => (
            <path
              key={i}
              d={`M${x} 70 Q${x - 4} 100 ${x + 3} 130 Q${x - 3} 150 ${x + 1} 160`}
              fill="none"
              stroke="hsl(var(--accent))"
              strokeOpacity="0.55"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          ))}
          <circle cx="70" cy="50" r="6" fill="white" opacity="0.7" />
        </svg>
      </div>
      <div className="absolute right-4 top-10 animate-float-slow" style={{ animationDelay: '1.2s' }}>
        <svg width="70" height="90" viewBox="0 0 70 90">
          <path d="M8 30 Q35 0 62 30 L62 35 Q35 30 8 35 Z" fill="hsl(var(--accent))" fillOpacity="0.5" />
          {smallTentacles.map((x, i) => (
            <path
              key={i}
              d={`M${x} 35 Q${x - 2} 55 ${x + 1} 80`}
              fill="none"
              stroke="hsl(var(--accent))"
              strokeOpacity="0.6"
              strokeWidth="1"
            />
          ))}
        </svg>
      </div>
      <div className="absolute left-3 bottom-3 font-mono text-[10px] tracking-[0.3em] text-primary/70">
        -2400m · ABYSS
      </div>
    </div>
  );
}

function TerminalFeed() {
  const lines = [
    '> initialize quantum.node',
    '  loading kernel...........[OK]',
    '  handshake digiflazz......[OK]',
    '  link ayolinx.tx..........[OK]',
    '> deploy --order=QTM-7341',
    '  signing.................[OK]',
    '  routing.................[OK]',
    '> drop sequence engaged_',
  ];
  const cols = Array.from({ length: 14 });
  const promptLabel = `${theme.brand.short.toLowerCase()}@core ~ %`;
  return (
    <div className="relative w-80 h-80 rounded-xl overflow-hidden bg-black border border-primary/60 shadow-[0_0_40px_rgba(16,185,129,0.25)]">
      <div className="absolute inset-0 opacity-60">
        {cols.map((_, i) => {
          const ch1 = String.fromCharCode(0x30a0 + (i * 13) % 96);
          const ch2 = String.fromCharCode(0x30a0 + (i * 7) % 96);
          const ch3 = String.fromCharCode(0x30a0 + (i * 23) % 96);
          const ch4 = String.fromCharCode(0x30a0 + (i * 17) % 96);
          const num = (i * 1297) % 9999;
          return (
            <span
              key={i}
              className="absolute top-0 font-mono text-[10px] text-primary animate-rain"
              style={{
                left: `${(i * 7.5) % 100}%`,
                animationDelay: `${(i * 0.4) % 3}s`,
                animationDuration: `${4 + (i % 4)}s`,
                writingMode: 'vertical-rl',
                textShadow: '0 0 6px currentColor',
              }}
            >
              {ch1}{ch2}{ch3} 01101 {ch4} {num}
            </span>
          );
        })}
      </div>
      <div className="absolute left-3 right-3 bottom-3 bg-black/85 border border-primary/50 rounded-md p-3 backdrop-blur-sm">
        <div className="flex items-center gap-1 mb-2">
          <span className="w-2 h-2 rounded-full bg-destructive/80" />
          <span className="w-2 h-2 rounded-full bg-accent/80" />
          <span className="w-2 h-2 rounded-full bg-primary/80" />
          <span className="ml-2 font-mono text-[10px] text-primary/70 tracking-widest">
            {promptLabel}
          </span>
        </div>
        {lines.map((ln, i) => (
          <div
            key={i}
            className="font-mono text-[10px] leading-relaxed text-primary"
            style={{ opacity: 0.55 + (i / lines.length) * 0.45 }}
          >
            {ln}
          </div>
        ))}
        <span className="inline-block w-2 h-3 bg-primary animate-pulse align-middle ml-1" />
      </div>
    </div>
  );
}

function DuneCaravan() {
  return (
    <div className="relative w-80 h-80 rounded-3xl overflow-hidden bg-gradient-to-b from-[#FCD9A4] via-[#E89766] to-[#7C2D12]">
      <div
        className="absolute top-10 right-12 w-28 h-28 rounded-full"
        style={{
          background: 'radial-gradient(circle, hsl(var(--accent)), hsl(var(--primary)) 70%, transparent 78%)',
          boxShadow: '0 0 80px hsl(var(--accent) / 0.7)',
        }}
      />
      <div className="absolute inset-0">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="absolute left-0 right-0 rounded-[50%]"
            style={{
              bottom: `${i * 18}%`,
              height: `${22 + i * 4}%`,
              background: `hsl(var(--secondary) / ${0.85 - i * 0.18})`,
              transform: `translateY(50%) scaleX(${1 + i * 0.15})`,
            }}
          />
        ))}
      </div>
      <svg className="absolute left-1/4 bottom-[34%]" width="120" height="60" viewBox="0 0 120 60" fill="hsl(var(--foreground))" fillOpacity="0.85">
        <path d="M10 50 L15 30 Q18 20 22 28 L25 22 Q28 16 31 24 L40 50 Z" />
        <circle cx="20" cy="38" r="2" />
        <path d="M50 50 L55 30 Q58 20 62 28 L65 20 Q68 14 71 22 L80 50 Z" />
        <circle cx="60" cy="38" r="2" />
        <path d="M88 50 L92 36 L96 32 L100 36 L104 50 Z" />
      </svg>
      <div className="absolute top-6 left-6 flex gap-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <span key={i} className="w-1 h-1 rounded-full bg-foreground/40" />
        ))}
      </div>
      <div className="absolute left-4 bottom-3 font-serif italic text-xs text-foreground/80 tracking-widest">
        ~ ROUTE 1462 · {theme.brand.short.toUpperCase()} ~
      </div>
    </div>
  );
}

function MoleculeOrbit() {
  return (
    <div className="relative w-80 h-80 flex items-center justify-center bg-card/30 rounded-3xl border border-border">
      <div
        className="absolute"
        style={{
          width: '85%',
          height: '40%',
          border: '2px solid hsl(var(--primary) / 0.45)',
          borderRadius: '50%',
        }}
      />
      <div
        className="absolute animate-spin-slow"
        style={{
          width: '85%',
          height: '40%',
          border: '2px solid hsl(var(--accent) / 0.55)',
          borderRadius: '50%',
          transform: 'rotate(60deg)',
        }}
      />
      <div
        className="absolute"
        style={{
          width: '85%',
          height: '40%',
          border: '2px solid hsl(var(--secondary) / 0.45)',
          borderRadius: '50%',
          transform: 'rotate(-60deg)',
        }}
      />
      <div
        className="absolute top-1/2 left-[10%] w-3 h-3 rounded-full bg-primary -translate-y-1/2 animate-orbit"
        style={{ boxShadow: '0 0 12px currentColor' }}
      />
      <div
        className="absolute top-1/2 right-[10%] w-2.5 h-2.5 rounded-full bg-accent -translate-y-1/2 animate-orbit"
        style={{ animationDelay: '1.2s', boxShadow: '0 0 10px currentColor' }}
      />
      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary via-accent to-secondary shadow-[0_0_40px_hsl(var(--primary)/0.6)] flex items-center justify-center">
        <div className="w-10 h-10 rounded-full bg-background/40 backdrop-blur-sm border border-white/40" />
      </div>
      <div className="absolute right-4 top-4 font-mono text-[10px] text-foreground/60 tracking-[0.3em]">
        H₂{theme.brand.short[0].toUpperCase()} · LAB
      </div>
    </div>
  );
}

function RuneCircle() {
  const runes = ['ᚠ', 'ᚢ', 'ᚦ', 'ᚨ', 'ᚱ', 'ᚲ', 'ᚷ', 'ᚹ', 'ᚺ', 'ᚾ', 'ᛁ', 'ᛃ'];
  return (
    <div className="relative w-80 h-80 flex items-center justify-center">
      <div className="absolute inset-0 rounded-full bg-gradient-radial from-accent/40 via-primary/10 to-transparent blur-2xl" />
      <div className="relative w-72 h-72 rounded-full border-4 border-accent/70 bg-card shadow-[0_0_60px_hsl(var(--accent)/0.5),inset_0_0_40px_hsl(var(--primary)/0.3)]">
        <div className="absolute inset-3 rounded-full border border-accent/40" />
        <div className="absolute inset-8 rounded-full border border-accent/30" />
        {runes.map((r, i) => {
          const angle = (i / runes.length) * 360;
          return (
            <div
              key={i}
              className="absolute left-1/2 top-1/2 font-serif text-2xl text-accent"
              style={{
                transform: `rotate(${angle}deg) translateY(-130px) rotate(-${angle}deg) translate(-50%, -50%)`,
                textShadow: '0 0 8px currentColor',
              }}
            >
              {r}
            </div>
          );
        })}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-24 h-24 rounded-full bg-accent/80 shadow-[0_0_30px_hsl(var(--accent))] flex items-center justify-center animate-pulse">
            <span className="font-serif text-4xl text-background font-bold">
              {theme.brand.short[0]}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function EnsoInk() {
  return (
    <div className="relative w-80 h-80 bg-card rounded-3xl overflow-hidden border border-border">
      <div className="absolute top-6 right-6 w-10 h-10">
        <div className="w-full h-full bg-accent" style={{ clipPath: 'polygon(50% 0, 100% 50%, 50% 100%, 0 50%)' }} />
      </div>
      <svg className="absolute inset-0 m-auto" width="280" height="280" viewBox="0 0 280 280">
        <defs>
          <filter id="brush">
            <feTurbulence baseFrequency="0.85" numOctaves="3" stitchTiles="stitch" />
            <feDisplacementMap in="SourceGraphic" scale="3" />
          </filter>
        </defs>
        <path
          d="M 80 140 A 60 60 0 1 1 200 140 A 60 60 0 0 1 140 200"
          fill="none"
          stroke="hsl(var(--foreground))"
          strokeWidth="14"
          strokeLinecap="round"
          filter="url(#brush)"
          opacity="0.92"
        />
      </svg>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="absolute w-3 h-4 rounded-full bg-accent/80 animate-float-slow"
          style={{
            left: `${20 + i * 30}%`,
            top: `${15 + i * 18}%`,
            transform: 'rotate(35deg)',
            animationDelay: `${i * 1.2}s`,
          }}
        />
      ))}
      <div className="absolute left-6 bottom-6 font-serif text-xs text-foreground/70" style={{ writingMode: 'vertical-rl' }}>
        無 ・ {theme.brand.short.toLowerCase()}
      </div>
    </div>
  );
}

function SunHorizon() {
  return (
    <div className="relative w-80 h-80 rounded-3xl overflow-hidden bg-gradient-to-b from-[#1E1B4B] via-[#7C3AED] to-[#0F172A]">
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 w-56 h-56 rounded-full overflow-hidden">
        <div className="w-full h-full bg-gradient-to-b from-accent via-primary to-secondary" />
        {[20, 35, 48, 60, 72, 84].map((y, i) => (
          <div
            key={i}
            className="absolute left-0 right-0 bg-background"
            style={{ top: `${y}%`, height: `${2 + i * 0.5}px` }}
          />
        ))}
      </div>
      <div
        className="absolute left-0 right-0 bottom-0 h-1/2"
        style={{
          background:
            'linear-gradient(180deg, transparent 0%, hsl(var(--background)) 100%), repeating-linear-gradient(0deg, transparent 0, transparent 14px, hsl(var(--primary) / 0.5) 14px, hsl(var(--primary) / 0.5) 15px), repeating-linear-gradient(90deg, transparent 0, transparent 28px, hsl(var(--primary) / 0.5) 28px, hsl(var(--primary) / 0.5) 29px)',
          perspective: '200px',
          transform: 'rotateX(60deg)',
          transformOrigin: 'top',
        }}
      />
      {[10, 25, 70, 88].map((left, i) => (
        <span
          key={i}
          className="absolute rounded-full bg-foreground animate-pulse"
          style={{ left: `${left}%`, top: `${5 + i * 4}%`, width: '2px', height: '2px' }}
        />
      ))}
      <div className="absolute right-4 top-4 font-mono text-xs text-accent/90 tracking-[0.3em]">
        ◢◤ {theme.brand.short.toUpperCase()}.84
      </div>
    </div>
  );
}

function CandleVigil() {
  return (
    <div className="relative w-80 h-80 rounded-3xl overflow-hidden bg-black border border-primary/30">
      <svg className="absolute left-0 top-0" width="120" height="120" viewBox="0 0 120 120" fill="none" stroke="hsl(var(--secondary) / 0.5)" strokeWidth="0.5">
        <path d="M0 0 L120 60 M0 0 L60 120 M0 0 L120 120 M0 0 L120 30 M0 0 L30 120" />
      </svg>
      <svg className="absolute right-0 bottom-0 rotate-180" width="120" height="120" viewBox="0 0 120 120" fill="none" stroke="hsl(var(--secondary) / 0.5)" strokeWidth="0.5">
        <path d="M0 0 L120 60 M0 0 L60 120 M0 0 L120 120 M0 0 L120 30 M0 0 L30 120" />
      </svg>
      <div className="absolute left-1/2 top-12 -translate-x-1/2 w-40 h-40 rounded-full bg-accent/30 blur-3xl animate-pulse" />
      <div className="absolute left-1/2 top-20 -translate-x-1/2">
        <div className="relative w-6 h-12">
          <div className="absolute left-1/2 top-0 -translate-x-1/2 w-3 h-12 bg-accent rounded-t-full animate-pulse" style={{ boxShadow: '0 0 16px hsl(var(--accent)), 0 0 30px hsl(var(--primary))' }} />
          <div className="absolute left-1/2 top-3 -translate-x-1/2 w-1.5 h-6 bg-foreground rounded-full" />
        </div>
      </div>
      <div className="absolute left-1/2 top-32 -translate-x-1/2 w-16 h-44 bg-gradient-to-b from-secondary via-foreground to-secondary/40 rounded-sm">
        <div className="absolute -bottom-2 left-0 right-0 h-4 bg-secondary blur-sm" />
        <div className="absolute top-0 left-1 right-1 h-2 bg-black/40" />
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="absolute bg-foreground/80 rounded-full"
            style={{
              left: `${15 + i * 30}%`,
              top: `${20 + i * 15}%`,
              width: '4px',
              height: `${20 + i * 6}px`,
              borderBottomLeftRadius: '50%',
              borderBottomRightRadius: '50%',
            }}
          />
        ))}
      </div>
      <div className="absolute left-4 bottom-4 font-serif italic text-xs text-primary/90 tracking-widest">
        † {theme.brand.short.toUpperCase()} · MMXXVI †
      </div>
    </div>
  );
}

function AstraFlux() {
  const particles = Array.from({ length: 18 });
  return (
    <div className="relative w-64 h-64 rounded-full border border-primary/20 bg-gradient-to-b from-[#0a0520] to-[#090314] overflow-hidden"
      style={{ boxShadow: '0 0 40px rgba(0,255,136,0.12), 0 0 80px rgba(255,0,170,0.06)' }}>

      {/* Background dot grid */}
      <div className="absolute inset-0 opacity-25" style={{
        backgroundImage: 'radial-gradient(circle, hsl(var(--primary) / 0.3) 1px, transparent 1px)',
        backgroundSize: '14px 14px',
      }} />

      {/* Scanline texture */}
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,136,0.08) 2px, rgba(0,255,136,0.08) 3px)',
      }} />

      {/* Space particles */}
      {particles.map((_, i) => {
        const left = (i * 53 + 7) % 100;
        const top = (i * 31 + 17) % 100;
        const size = 1 + (i % 2.5);
        const delay = (i * 0.35) % 4;
        const dur = 2.5 + (i % 3);
        const isGreen = i % 3 !== 2;
        return (
          <span key={i} className="absolute rounded-full animate-pulse"
            style={{
              left: `${left}%`, top: `${top}%`,
              width: `${size}px`, height: `${size}px`,
              background: isGreen ? 'hsl(var(--primary))' : 'hsl(var(--secondary))',
              boxShadow: isGreen
                ? '0 0 4px hsl(var(--primary)), 0 0 8px hsl(var(--primary))'
                : '0 0 4px hsl(var(--secondary)), 0 0 8px hsl(var(--secondary))',
              animationDelay: `${delay}s`,
              animationDuration: `${dur}s`,
            }}
          />
        );
      })}

      {/* Orbit 1 — neon green dashed */}
      <div className="absolute inset-6 rounded-full animate-spin-slow" style={{
        border: '1px dashed hsl(var(--primary) / 0.5)',
        transform: 'rotate(25deg)',
        boxShadow: '0 0 8px hsl(var(--primary) / 0.2)',
        animationDuration: '18s',
      }} />

      {/* Orbit 2 — magenta solid */}
      <div className="absolute inset-14 rounded-full animate-spin" style={{
        border: '1.5px solid hsl(var(--secondary) / 0.6)',
        transform: 'rotate(-45deg)',
        boxShadow: '0 0 10px hsl(var(--secondary) / 0.3)',
        animationDuration: '7s',
      }} />

      {/* Orbit 3 — accent violet faint */}
      <div className="absolute inset-[72px] rounded-full animate-spin-slow" style={{
        border: '1px solid hsl(var(--accent) / 0.3)',
        animationDuration: '30s',
        animationDirection: 'reverse',
      }} />

      {/* SVG ellipse orbits */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 256 256">
        <ellipse cx="128" cy="128" rx="110" ry="32" fill="none"
          stroke="url(#afGrad1)" strokeWidth="1.2" strokeDasharray="4,8"
          transform="rotate(-20 128 128)"
          style={{ animation: 'spinSlow 50s linear infinite' }} />
        <ellipse cx="128" cy="128" rx="80" ry="22" fill="none"
          stroke="url(#afGrad2)" strokeWidth="1.2"
          transform="rotate(40 128 128)"
          style={{ animation: 'spinSlow 35s linear infinite reverse' }} />
        <defs>
          <linearGradient id="afGrad1" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.9" />
            <stop offset="100%" stopColor="hsl(var(--secondary))" stopOpacity="0.3" />
          </linearGradient>
          <linearGradient id="afGrad2" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="hsl(var(--secondary))" stopOpacity="0.8" />
            <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity="0.2" />
          </linearGradient>
        </defs>
      </svg>

      {/* Pulsar core */}
      <div className="absolute inset-0 flex items-center justify-center animate-float" style={{ animationDuration: '5s' }}>
        <div className="relative w-20 h-20 flex items-center justify-center">
          {/* Outer glow layers */}
          <div className="absolute w-20 h-20 rounded-full blur-xl animate-pulse"
            style={{ background: 'radial-gradient(circle, hsl(var(--primary) / 0.3), transparent)', animationDuration: '2s' }} />
          <div className="absolute w-14 h-14 rounded-full blur-md"
            style={{ background: 'radial-gradient(circle, hsl(var(--secondary) / 0.35), transparent)' }} />
          <div className="absolute w-9 h-9 rounded-full blur-sm animate-ping"
            style={{ background: 'hsl(var(--primary) / 0.2)', animationDuration: '2.5s' }} />
          {/* Core sphere */}
          <div className="w-8 h-8 rounded-full flex items-center justify-center z-10"
            style={{
              background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--secondary)))',
              boxShadow: '0 0 20px hsl(var(--primary) / 0.8), 0 0 40px hsl(var(--primary) / 0.4)',
            }}>
            <div className="w-2.5 h-2.5 rounded-full bg-white" style={{ boxShadow: '0 0 8px #fff' }} />
          </div>
          {/* Cross flares */}
          <div className="absolute w-1 h-12 rounded-full animate-pulse"
            style={{ background: 'linear-gradient(to bottom, transparent, hsl(var(--primary) / 0.6), transparent)', animationDuration: '2s' }} />
          <div className="absolute h-1 w-12 rounded-full animate-pulse"
            style={{ background: 'linear-gradient(to right, transparent, hsl(var(--secondary) / 0.5), transparent)', animationDuration: '2.5s' }} />
        </div>
      </div>

      {/* HUD labels */}
      <div className="absolute bottom-3 left-0 right-0 text-center font-mono text-[8px] tracking-[0.35em] animate-flicker"
        style={{ color: 'hsl(var(--primary) / 0.7)' }}>
        [ FLUX_CORE_ACTIVE ]
      </div>
      <div className="absolute top-3 left-4 font-mono text-[7px] tracking-widest"
        style={{ color: 'hsl(var(--muted-foreground))' }}>
        SEC:ORBIT_Σ
      </div>
      <div className="absolute top-3 right-4 font-mono text-[7px]"
        style={{ color: 'hsl(var(--secondary) / 0.6)' }}>
        ● SYNC
      </div>
    </div>
  );
}

function AetherCore() {
  const steamPuffs = Array.from({ length: 8 });
  return (
    <div className="relative w-80 h-80 flex items-center justify-center bg-card/25 rounded-full border border-primary/20 shadow-[0_0_30px_rgba(230,138,0,0.15)] overflow-hidden">
      {/* Background drafting lines */}
      <div className="absolute inset-0 opacity-15" style={{
        backgroundImage: 'radial-gradient(circle, hsl(var(--primary) / 0.3) 1px, transparent 1px)',
        backgroundSize: '16px 16px',
      }} />

      {/* Steam / Aether particle puffs */}
      {steamPuffs.map((_, i) => {
        const left = 35 + (i * 7) % 30;
        const delay = i * 0.4;
        const scale = 0.5 + (i % 2) * 0.5;
        return (
          <div
            key={i}
            className="absolute rounded-full bg-secondary/30 blur-sm animate-pulse pointer-events-none"
            style={{
              left: `${left}%`,
              bottom: '15%',
              width: `${12 * scale}px`,
              height: `${12 * scale}px`,
              animation: `float-slow 3s infinite ease-out`,
              animationDelay: `${delay}s`,
              transform: `translateY(-${(i * 12) % 60}px)`,
            }}
          />
        );
      })}

      {/* Outer riveted pressure ring */}
      <div className="absolute w-72 h-72 rounded-full border-4 border-[#b08b5c]/70 flex items-center justify-center animate-spin-slow">
        {/* Mock rivets around the ring */}
        {Array.from({ length: 12 }).map((_, i) => {
          const angle = (i / 12) * 360;
          return (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full bg-gradient-to-r from-primary to-amber-800 border border-black/50"
              style={{
                transform: `rotate(${angle}deg) translateY(-140px)`,
              }}
            />
          );
        })}
      </div>

      {/* Counter-rotating gear 1 (Brass/Amber, Large) */}
      <div className="absolute w-56 h-56 flex items-center justify-center opacity-75 animate-spin-slow" style={{ animationDirection: 'reverse' }}>
        <svg className="w-full h-full fill-[#e68a00]" viewBox="0 0 100 100">
          <path d="M50 35c-8.3 0-15 6.7-15 15s6.7 15 15 15 15-6.7 15-15-6.7-15-15-15zm0 24c-5 0-9-4-9-9s4-9 9-9 9 4 9 9-4 9-9 9z" />
          {/* Gear teeth */}
          {Array.from({ length: 10 }).map((_, i) => (
            <path
              key={i}
              d="M47 12h6l2 8h-10z"
              transform={`rotate(${i * 36} 50 50)`}
            />
          ))}
          <circle cx="50" cy="50" r="28" fill="none" stroke="#e68a00" strokeWidth="2" strokeDasharray="3,5" />
        </svg>
      </div>

      {/* Rotating gear 2 (Copper/Flame, Medium) */}
      <div className="absolute w-36 h-36 flex items-center justify-center opacity-90 animate-spin" style={{ animationDuration: '8s' }}>
        <svg className="w-full h-full fill-[#df3b15]" viewBox="0 0 100 100">
          <path d="M50 40c-5.5 0-10 4.5-10 10s4.5 10 10 10 10-4.5 10-10-4.5-10-10-10z" />
          {/* Gear teeth */}
          {Array.from({ length: 8 }).map((_, i) => (
            <path
              key={i}
              d="M48 20h4l1 6h-6z"
              transform={`rotate(${i * 45} 50 50)`}
            />
          ))}
        </svg>
      </div>

      {/* Rotating gear 3 (Small, Steel/Dark) */}
      <div className="absolute w-20 h-20 flex items-center justify-center opacity-80 animate-spin-slow" style={{ animationDuration: '4s', animationDirection: 'reverse' }}>
        <svg className="w-full h-full fill-[#3d342c]" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="15" />
          {Array.from({ length: 6 }).map((_, i) => (
            <path
              key={i}
              d="M48 30h4l1 8h-6z"
              transform={`rotate(${i * 60} 50 50)`}
            />
          ))}
        </svg>
      </div>

      {/* Reactor Aetherial Core */}
      <div className="absolute w-16 h-16 flex items-center justify-center">
        {/* Glowing aura */}
        <div className="absolute inset-0 rounded-full bg-secondary/30 blur-md animate-ping" style={{ animationDuration: '3s' }} />
        <div className="absolute inset-2 rounded-full bg-gradient-to-tr from-secondary to-primary animate-pulse shadow-[0_0_20px_hsl(var(--secondary))]" />
        {/* Core center light */}
        <div className="absolute w-4 h-4 rounded-full bg-white shadow-[0_0_8px_#fff]" />
        
        {/* Gauge needle indicator overlay */}
        <div className="absolute w-1.5 h-12 bg-accent origin-bottom bottom-1/2 left-[calc(50%-3px)] rounded-full animate-flicker" />
      </div>

      {/* Analog HUD status tags */}
      <div className="absolute bottom-4 left-0 right-0 text-center font-mono text-[9px] tracking-[0.3em] text-secondary animate-pulse">
        [ AETHER_PRESSURE_OK ]
      </div>
      <div className="absolute top-4 left-6 font-mono text-[7px] text-muted-foreground tracking-widest">
        BOILER_NO: 04-ATC
      </div>
      <div className="absolute top-4 right-6 font-mono text-[7px] text-primary">
        ● CONNECTED
      </div>
    </div>
  );
}

const VARIANTS = {
  'controller-orb': ControllerOrb,
  'circuit-grid': CircuitGrid,
  'pixel-tiles': PixelTiles,
  'gold-orbs': GoldOrbs,
  'crosshair-radar': CrosshairRadar,
  'deep-abyss': DeepAbyss,
  'terminal-feed': TerminalFeed,
  'dune-caravan': DuneCaravan,
  'molecule-orbit': MoleculeOrbit,
  'rune-circle': RuneCircle,
  'enso-ink': EnsoInk,
  'sun-horizon': SunHorizon,
  'candle-vigil': CandleVigil,
  'astra-flux': AstraFlux,
  'aether-core': AetherCore,
};

export default function HeroVisual({ className = '', compact = false }) {
  const Component = VARIANTS[VARIANT] || ControllerOrb;
  return (
    <div
      data-testid="hero-visual"
      className={`relative ${compact ? 'scale-75' : ''} ${className}`}
    >
      <Component />
    </div>
  );
}
